import { normalize, scoreToken, words } from "./layerSearch";

/**
 * CHERCHER UNE MESURE DANS UN CATALOGUE DE DEUX CENTS.
 *
 * Le sélecteur de mesures cherchait par sous-chaîne sur l'identifiant et le
 * titre. Deux conséquences : « végétalisation » ne trouvait pas
 * « Végétaliser », et rien ne permettait de descendre à « les mesures ACG qui
 * contraignent l'agriculture » — alors que c'est exactement comme ça qu'on
 * garnit un réservoir : par thème, pas mesure par mesure.
 *
 * DEUX MÉCANISMES, ET RIEN DE PLUS.
 *
 * 1. LA RECHERCHE TEXTUELLE réutilise celle du catalogue de couches
 *    (`layerSearch`) : tolérante aux fautes et aux accents, le titre pesant
 *    plus que le reste. Une seconde implémentation de la même idée finirait
 *    par ne plus se comporter pareil selon l'écran.
 *
 * 2. LES FACETTES SONT LUES DANS LE CATALOGUE, jamais déclarées ici. Les
 *    étiquettes (`tags`) et la classification ACG (`metadata.sector`, `type`,
 *    `category`) existent déjà dans les documents ; une liste de valeurs
 *    écrite en dur mentirait dès la première mesure ajoutée. Ce module ne fixe
 *    donc que les CHAMPS où regarder — et une facette dont toutes les valeurs
 *    couvrent tout le catalogue ne filtre rien : elle n'est pas proposée.
 */

/* Les champs à facettes. L'ordre est celui de l'affichage : l'étiquette
   d'abord, parce que c'est le seul champ qu'un auteur remplit à la main. */
export const FACET_GROUPS = [
  { key: "tags", label: "Étiquettes", valuesOf: (d) => d.tags || [] },
  { key: "sector", label: "Secteur", valuesOf: (d) => [d.metadata?.sector] },
  { key: "type", label: "Levier", valuesOf: (d) => [d.metadata?.type] },
  { key: "category", label: "Intention", valuesOf: (d) => [d.metadata?.category] },
];

function valuesOf(group, decision) {
  return [...new Set(
    (group.valuesOf(decision || {}) || [])
      .filter((v) => typeof v === "string" && v.trim())
      .map((v) => v.trim())
  )];
}

/**
 * Un `sobriete_hydrique` de base rendu lisible. Aucune table de traduction :
 * elle serait à compléter à chaque valeur inventée dans la base, et une valeur
 * absente de la table s'afficherait alors moins bien que sa version brute.
 */
export function facetValueLabel(value) {
  const text = String(value || "").replace(/[_-]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

/**
 * Les facettes réellement utilisables sur CE catalogue, valeurs comptées et
 * rangées par fréquence.
 *
 * Une facette est écartée quand elle ne peut rien restreindre : une seule
 * valeur, portée par toutes les mesures. Un filtre qui ne filtre pas se
 * clique une fois, ne change rien, et fait douter de tous les autres.
 */
export function facetsOf(decisions) {
  const list = decisions || [];
  return FACET_GROUPS
    .map((group) => {
      const counts = new Map();
      list.forEach((decision) => {
        valuesOf(group, decision).forEach((value) => {
          counts.set(value, (counts.get(value) || 0) + 1);
        });
      });
      const values = [...counts.entries()]
        .map(([value, count]) => ({ value, count, label: facetValueLabel(value) }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "fr"));
      return { key: group.key, label: group.label, values };
    })
    .filter((group) => (
      group.values.length > 1
      || (group.values.length === 1 && group.values[0].count < list.length)
    ));
}

/**
 * LES VALEURS DE FACETTE D'UNE SEULE MESURE, pour l'afficher.
 *
 * Distinct de `facetsOf`, qui répond à une autre question : « quels filtres ce
 * catalogue permet-il ? » — et écarte donc les facettes qui ne restreignent
 * rien. Sur une mesure isolée, toute valeur est bonne à montrer ; les appeler
 * l'une pour l'autre rendait une liste vide.
 */
export function decisionFacetValues(decision) {
  return FACET_GROUPS.flatMap((group) => (
    valuesOf(group, decision || {}).map((value) => ({
      key: group.key,
      value,
      label: facetValueLabel(value),
    }))
  ));
}

/** Index plat : une entrée par mesure, ses mots et ses valeurs de facettes. */
export function buildDecisionIndex(decisions) {
  return (decisions || []).filter((d) => d && d._id).map((decision) => {
    const facets = {};
    FACET_GROUPS.forEach((group) => { facets[group.key] = valuesOf(group, decision); });
    const facetWords = Object.values(facets).flat().join(" ");
    return {
      id: decision._id,
      decision,
      title: decision.title || decision._id,
      // Le titre pèse plus que le reste : une correspondance sur le nom de la
      // mesure doit passer devant une correspondance sur sa description.
      primary: words(decision.title || decision._id),
      secondary: words([decision._id, decision.description, facetWords].join(" ")),
      facets,
    };
  });
}

/**
 * Les mesures qui passent les filtres, les plus pertinentes d'abord.
 *
 * `facets` est de la forme `{ tags: ["acg"], sector: ["public", "agriculture"] }` :
 * OU à l'intérieur d'un groupe, ET entre les groupes. C'est la lecture
 * naturelle d'une série de cases cochées — « ACG, et (public ou agriculture) ».
 *
 * Sans terme de recherche, tous les scores valent zéro et il ne reste que
 * l'ordre alphabétique : une liste stable, dans laquelle on peut pointer une
 * mesure de mémoire.
 */
export function searchDecisions(index, { query = "", facets = {}, limit = 0 } = {}) {
  const active = Object.entries(facets || {}).filter(([, values]) => values && values.length);
  const tokens = normalize(query).split(/\s+/).filter(Boolean);

  const scored = [];
  for (const entry of index || []) {
    const passesFacets = active.every(([key, values]) => (
      values.some((value) => (entry.facets[key] || []).includes(value))
    ));
    if (!passesFacets) continue;

    let total = 0;
    let matchesAll = true;
    for (const token of tokens) {
      const score = Math.max(
        scoreToken(token, entry.primary),
        scoreToken(token, entry.secondary) * 0.5
      );
      // Tous les termes doivent correspondre : « interdire golf » ne doit pas
      // remonter tout ce qui parle d'interdiction.
      if (score === 0) { matchesAll = false; break; }
      total += score;
    }
    if (matchesAll) scored.push({ entry, score: total });
  }

  scored.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title, "fr"));
  const results = scored.map((s) => s.entry.decision);
  return limit > 0 ? results.slice(0, limit) : results;
}
